import * as React from 'react';
import styled, {css} from 'styled-components';
import {Container as Button} from 'components/Button';
import {ButtonRounded} from './styled';
import {
  getFormattedDate,
  getDiscountPercent,
} from '../helpers';
import {Countdown} from 'components/Countdown';
import {CoursePromoContext} from '../context';
import {DateFormat} from '../types';

export interface DiscountBannerProps {
  onClose: () => void;
}

/**
 * The function return base component font styles
 * @return {
 *   FlattenInterpolation<ThemedStyledProps<{}, AnyIfEmpty<DefaultTheme>>>
 * }
 */
function getFontStyles() {
  return css`
    font-size: 24px;
    font-weight: 700;
    line-height: 29px;
  `;
}

const Container = styled.div`
  height: 70px;
  color: ${({theme}) => theme.variables.accentBackgroundColor};;
  background-color: ${({theme}) => theme.colors.goldenLight};
  box-shadow: 5px 5px 60px rgba(24, 24, 24, 0.8);
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  margin: 0 auto;
  max-width: 1160px;
`;

const Discount = styled.div`
  margin-right: auto;
  ${getFontStyles()};
`;

const Timer = styled.div`
  margin-right: 35px;
  ${getFontStyles()};
`;

const Buttons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, max-content);
  align-items: center;
  width: max-content;
  gap: 10px;
  
  ${Button} {
    margin: 0;
    padding: 0;
    gap: 0;
  }
`;

const ButtonClose = styled.div`
  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 50px;
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg width='50' height='50' viewBox='0 0 50 50' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M26.5556 25L31.2222 20.3333C31.6518 19.9038 31.6518 19.2073 31.2222 18.7778C30.7927 18.3482 30.0962 18.3482 29.6667 18.7778L25 23.4444L20.3333 18.7778C19.9038 18.3482 19.2073 18.3482 18.7778 18.7778C18.3482 19.2073 18.3482 19.9038 18.7778 20.3333L23.4444 25L18.7778 29.6667C18.3482 30.0962 18.3482 30.7927 18.7778 31.2222C19.2073 31.6518 19.9038 31.6518 20.3333 31.2222L25 26.5556L29.6667 31.2222C30.0962 31.6518 30.7927 31.6518 31.2222 31.2222C31.6518 30.7927 31.6518 30.0962 31.2222 29.6667L26.5556 25Z' fill='%2329292B'/%3E%3C/svg%3E%0A");
  }
`;

const ButtonInstallment = styled(ButtonRounded)`
  border: 1px solid #29292B;
  background-color: transparent;
  color: ${({theme}) => theme.variables.accentBackgroundColor};;
`;

/**
 * The returns course promo discount banner component
 * @return {React.ReactElement}
 * @constructor
 */
export default function DiscountBanner({onClose}: DiscountBannerProps)
  : React.ReactElement | null {
  const context = React.useContext(CoursePromoContext);
  const {
    old,
    current,
    discountEndDate,
    isInstallmentAvailable,
  } = context.prices;

  const discountPercent = old && getDiscountPercent(old, current);

  if (! discountPercent) {
    return null;
  }

  return (
    <Container>
      <Content>
        <Discount>
          Скидка {discountPercent}%
          {
            discountEndDate
            && ` до ${
              getFormattedDate(
                  discountEndDate,
                  DateFormat.DayMonth,
              )
            }!`
          }
        </Discount>
        {discountEndDate && (
          <Timer>
            <Countdown
              timeTillDate={discountEndDate}
            />
          </Timer>
        )}
        <Buttons>
          {
            isInstallmentAvailable && (
              <Button>
                <ButtonInstallment>В рассрочку</ButtonInstallment>
              </Button>
            )
          }
          <Button>
            <ButtonRounded>
              Записаться на курс
            </ButtonRounded>
          </Button>
          <ButtonClose onClick={onClose}/>
        </Buttons>
      </Content>
    </Container>
  );
}
